import { useRef, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import constate from 'constate';

import { LocalStore } from 'constants/localstore';
import { FileStatus } from 'services/file.service';
import { LogLevel } from 'services/logger.service';
import { GOOGLE_SCOPE_PICKER } from 'services/google/google.params';
import {
  createSession,
  getSessionData,
  GoogleMediaItem,
  GoogleApiError,
  deleteSession,
  Session,
} from 'services/google/photos.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { ModalEnum, setModal } from 'components/app/_mediator/app.slice';
import { appLogger } from 'components/app/_mediator/app-logger.slice';
import { uploadFiles, uploadGFiles } from 'components/app/_mediator/app-files-upload.slice';

type PickerFile = File | GoogleMediaItem;
type PickerReplace = {
  newFiles: PickerFile[];
  existingFiles: PickerFile[];
};

const usePickerProvider = () => {
  const files = useAppSelector((state) => state.app__files.files);
  const session = useRef<Session | null>(null);
  const dispatch = useAppDispatch();

  const [pickerReplaceData, setPickerReplaceData] = useState<PickerReplace | null>(null);

  const gpLogin = useGoogleLogin({
    onSuccess: (res) => {
      localStorage.setItem(LocalStore.googleOAuth, res.access_token);
      localStorage.setItem(LocalStore.googleOAuthDate, Date.now().toString());
      gpInit();
    },
    onError: (err) => console.log(err),
    scope: GOOGLE_SCOPE_PICKER,
  });

  const gpCleanSession = (id: string) => {
    session.current = null;
    deleteSession(id);
  };

  const gpErr = (err: GoogleApiError) => {
    if (err === '401' || err === '403') {
      if (err === '403')
        dispatch(appLogger({ level: LogLevel.Error, error: { name: 'googlePicker', message: 'token expired time' } }));
      gpLogin();
    } else
      dispatch(
        appLogger({
          level: LogLevel.Error,
          error: { name: 'googlePicker', message: err.message, stack: JSON.stringify(err.response) },
        })
      );
  };

  const gpReLogin = () => {
    localStorage.removeItem(LocalStore.googleOAuth);
    localStorage.removeItem(LocalStore.googleOAuthDate);
    closeModal();
    gpLogin();
  };

  const gpInit = async () => {
    try {
      const sessionId = session.current?.id;
      if (sessionId) gpCleanSession(sessionId);
      const res = await createSession();
      session.current = res;
      dispatch(setModal(ModalEnum.gpOpen));
    } catch (err) {
      gpErr(err as GoogleApiError);
    }
  };

  const gpOpen = () => {
    const pickerUri = session.current?.pickerUri;
    if (!pickerUri) {
      closeModal();
      return;
    }
    window.open(pickerUri, '_blank', 'width=800,height=600,top=100,left=200,resizable=yes');
    dispatch(setModal(ModalEnum.gpSubmit));
  };

  const gpSubmit = async () => {
    try {
      const sessionId = session.current?.id;
      if (!sessionId) return;
      const mediaItems = (await getSessionData(sessionId)).mediaItems;
      gpCleanSession(sessionId);
      const shouldCloseModal = onPick(mediaItems);
      if (shouldCloseModal) {
        dispatch(setModal(0));
      }
    } catch (err) {
      gpErr(err as GoogleApiError);
      dispatch(setModal(0));
    }
  };

  /* *** */

  const onPick = (payload: PickerFile[]) => {
    let shouldCloseModal = true;

    setPickerReplaceData(null);
    const newFiles: PickerFile[] = [];
    const existingFiles: PickerFile[] = [];
    payload.forEach((el) => {
      const isFileExists = Object.values(files).some(
        (file) => file.fileName === getFileName(el) && file.status !== FileStatus.UploadInterrupted
      );
      if (isFileExists) existingFiles.push(el);
      else newFiles.push(el);
    });

    if (existingFiles.length) {
      setPickerReplaceData({ newFiles, existingFiles });
      dispatch(setModal(ModalEnum.replace));
      shouldCloseModal = false;
    } else {
      onUpload(payload);
    }

    return shouldCloseModal;
  };

  const onUpload = async (payload: PickerFile[]) => {
    const reUpload = payload.some((el) => Object.values(files).some((file) => file.fileName === getFileName(el)));
    if (payload[0] instanceof File) dispatch(uploadFiles({ files: payload as File[], reUpload }));
    else dispatch(uploadGFiles({ files: payload as GoogleMediaItem[], reUpload }));
    closeModal();
  };

  const closeModal = () => {
    dispatch(setModal(0));
  };

  return { gpReLogin, gpInit, gpOpen, gpSubmit, pickerReplaceData, onPick, onUpload, closeModal };
};

export const [PickerProvider, usePickerContext] = constate(usePickerProvider);

export const getFileName = (file: PickerFile) => {
  if (file instanceof File) return file.name;
  return (file as GoogleMediaItem).mediaFile.filename;
};
